import type { SkillRequirement } from "../engine/types"

/**
 * Demo role catalogue + role→required-skills map.
 *
 * In production this is SuccessFactors **Job Profile Builder** (required
 * attributes per job role). Here it is hand-authored, and the seed script
 * assigns roles to the anonymised org population by org unit.
 */

export interface Role {
    id: string
    label: string
    /**
     * Which org-unit name fragments this role plausibly belongs to. The seed
     * uses these to distribute roles over the real org structure so that an
     * "IT Risk & BCM" employee does not come out as a Kundenberater.
     */
    unitHints: string[]
    /** Roughly how common the role is inside a matching unit (weight). */
    weight: number
    /** True for roles that supervise — the manager view needs some of these. */
    isLeadership?: boolean
}

export const ROLES: Role[] = [
    {
        id: "kundenberater",
        label: "Kundenberater:in Privatkunden",
        unitHints: ["beratung", "privatkunden", "niederlassung", "vertrieb", "filiale", "region"],
        weight: 5,
    },
    {
        id: "anlageberater",
        label: "Anlageberater:in",
        unitHints: ["anlage", "investment", "vermögens", "private banking", "wealth"],
        weight: 3,
    },
    {
        id: "finanzplaner",
        label: "Finanzplaner:in",
        unitHints: ["finanzplanung", "vorsorge", "wealth", "private banking"],
        weight: 2,
    },
    {
        id: "hypothekarberater",
        label: "Hypothekarberater:in",
        unitHints: ["hypothek", "finanzierung", "immobilien", "wohneigentum"],
        weight: 3,
    },
    {
        id: "kreditanalyst",
        label: "Kreditanalyst:in",
        unitHints: ["kredit", "credit", "risk", "risiko", "recovery", "bonität"],
        weight: 3,
    },
    {
        id: "firmenkundenberater",
        label: "Firmenkundenberater:in",
        unitHints: ["firmenkunden", "corporate", "kmu", "geschäftskunden"],
        weight: 2,
    },
    {
        id: "compliance-officer",
        label: "Compliance Officer",
        unitHints: ["compliance", "legal", "recht", "geldwäscherei", "aml"],
        weight: 3,
    },
    {
        id: "risk-manager",
        label: "Risk Manager:in",
        unitHints: ["risk", "risiko", "bcm", "op risk", "kontroll"],
        weight: 3,
    },
    {
        id: "it-engineer",
        label: "IT Engineer",
        unitHints: [
            "it",
            "informatik",
            "engineering",
            "software",
            "infrastruktur",
            "data",
            "automation",
            "digital",
            "technology",
        ],
        weight: 5,
    },
    {
        id: "it-security",
        label: "Security Engineer",
        unitHints: ["security", "sicherheit", "infosec", "cyber"],
        weight: 2,
    },
    {
        id: "projektleiter",
        label: "Projektleiter:in",
        unitHints: ["projekt", "pmo", "transformation", "change", "organisation"],
        weight: 3,
    },
    {
        id: "operations",
        label: "Sachbearbeiter:in Operations",
        unitHints: ["operations", "abwicklung", "zahlungsverkehr", "service", "backoffice", "center"],
        weight: 4,
    },
    {
        id: "hr-specialist",
        label: "HR Specialist",
        unitHints: ["hr", "personal", "human"],
        weight: 2,
    },
    {
        id: "teamleiter",
        label: "Teamleiter:in",
        unitHints: [],
        weight: 1,
        isLeadership: true,
    },
    {
        id: "lernender",
        label: "Lernende:r / Nachwuchs",
        unitHints: [],
        weight: 1,
    },
]

export const ROLE_BY_ID: ReadonlyMap<string, Role> = new Map(ROLES.map((r) => [r.id, r]))

/** Shorthand so the map below stays readable. */
const req = (
    skillId: string,
    level: 0 | 1 | 2 | 3 | 4,
    origin: SkillRequirement["origin"] = "rolle",
): SkillRequirement => ({ skillId, level, origin })

/**
 * Role → required skills.
 *
 * `regulatorik`-origin entries are the skill-side shadow of an obligation:
 * the obligation (see `obligations.ts`) is what actually forces the Baustein
 * into the plan, but the requirement keeps the skill visible in the gap view.
 */
export const ROLE_REQUIREMENTS: Record<string, SkillRequirement[]> = {
    kundenberater: [
        req("reg.fidleg", 3, "regulatorik"),
        req("reg.suitability", 2, "regulatorik"),
        req("reg.gwg", 2, "regulatorik"),
        req("reg.vsb", 2, "regulatorik"),
        req("reg.datenschutz", 2, "regulatorik"),
        req("reg.infosec", 2, "regulatorik"),
        req("fach.beratungsprozess", 3),
        req("fach.gespraechsfuehrung", 3),
        req("fach.produktkenntnis", 2),
        req("fach.vorsorge", 2),
        req("fach.anlageberatung", 2),
        req("fach.hypotheken", 2),
        req("fach.esgBeratung", 2),
        req("dig.digitaleKanaele", 2),
        req("dig.aiRisiken", 2, "regulatorik"),
    ],
    anlageberater: [
        req("reg.fidleg", 3, "regulatorik"),
        req("reg.suitability", 3, "regulatorik"),
        req("reg.gwg", 2, "regulatorik"),
        req("reg.marktverhalten", 2, "regulatorik"),
        req("reg.infosec", 2, "regulatorik"),
        req("fach.anlageberatung", 3),
        req("fach.portfolio", 3),
        req("fach.wertschriften", 3),
        req("fach.esgBeratung", 3),
        req("fach.gespraechsfuehrung", 3),
        req("dig.aiRisiken", 2, "regulatorik"),
    ],
    finanzplaner: [
        req("reg.fidleg", 3, "regulatorik"),
        req("reg.suitability", 3, "regulatorik"),
        req("reg.gwg", 2, "regulatorik"),
        req("reg.infosec", 2, "regulatorik"),
        req("fach.finanzplanung", 3),
        req("fach.vorsorge", 3),
        req("fach.steuern", 3),
        req("fach.anlageberatung", 2),
        req("met.praesentation", 2),
        req("dig.aiRisiken", 2, "regulatorik"),
    ],
    hypothekarberater: [
        req("reg.fidleg", 2, "regulatorik"),
        req("reg.gwg", 2, "regulatorik"),
        req("reg.vsb", 2, "regulatorik"),
        req("reg.infosec", 2, "regulatorik"),
        req("fach.hypotheken", 3),
        req("fach.tragbarkeit", 3),
        req("fach.immobilienbewertung", 2),
        req("fach.kreditanalyse", 2),
        req("fach.gespraechsfuehrung", 3),
        req("dig.aiRisiken", 2, "regulatorik"),
    ],
    kreditanalyst: [
        req("reg.gwg", 2, "regulatorik"),
        req("reg.infosec", 2, "regulatorik"),
        req("reg.opRisk", 2),
        req("fach.kreditanalyse", 3),
        req("fach.kreditrisiko", 3),
        req("fach.bilanzanalyse", 3),
        req("fach.tragbarkeit", 3),
        req("fach.immobilienbewertung", 2),
        req("dig.excel", 2),
        req("dig.aiRisiken", 2, "regulatorik"),
    ],
    firmenkundenberater: [
        req("reg.fidleg", 2, "regulatorik"),
        req("reg.gwg", 3, "regulatorik"),
        req("reg.vsb", 3, "regulatorik"),
        req("reg.sanktionen", 2, "regulatorik"),
        req("reg.infosec", 2, "regulatorik"),
        req("fach.firmenkunden", 3),
        req("fach.bilanzanalyse", 3),
        req("fach.kreditanalyse", 3),
        req("fach.zahlungsverkehr", 2),
        req("met.verhandlung", 3),
        req("dig.aiRisiken", 2, "regulatorik"),
    ],
    "compliance-officer": [
        req("reg.gwg", 4, "regulatorik"),
        req("reg.vsb", 4, "regulatorik"),
        req("reg.kyc", 4),
        req("reg.sanktionen", 3, "regulatorik"),
        req("reg.fidleg", 3, "regulatorik"),
        req("reg.datenschutz", 3, "regulatorik"),
        req("reg.marktverhalten", 3),
        req("reg.infosec", 2, "regulatorik"),
        req("met.schreiben", 3),
        req("dig.aiRisiken", 2, "regulatorik"),
    ],
    "risk-manager": [
        req("reg.opRisk", 3),
        req("reg.bcm", 3),
        req("reg.outsourcing", 3),
        req("reg.infosec", 3, "regulatorik"),
        req("reg.datenschutz", 2, "regulatorik"),
        req("fach.kreditrisiko", 2),
        req("dig.datenanalyse", 2),
        req("dig.excel", 3),
        req("dig.aiRisiken", 2, "regulatorik"),
    ],
    "it-engineer": [
        req("reg.infosec", 3, "regulatorik"),
        req("reg.datenschutz", 2, "regulatorik"),
        req("dig.softwareEngineering", 3),
        req("dig.cloudGrundlagen", 2),
        req("dig.securityEngineering", 2),
        req("met.agile", 2),
        req("dig.aiGrundlagen", 2),
        req("dig.aiRisiken", 2, "regulatorik"),
    ],
    "it-security": [
        req("reg.infosec", 4, "regulatorik"),
        req("reg.datenschutz", 3, "regulatorik"),
        req("reg.opRisk", 2),
        req("dig.securityEngineering", 4),
        req("dig.cloudGrundlagen", 3),
        req("dig.aiRisiken", 3, "regulatorik"),
    ],
    projektleiter: [
        req("reg.infosec", 2, "regulatorik"),
        req("reg.datenschutz", 2, "regulatorik"),
        req("met.projektmanagement", 3),
        req("met.agile", 2),
        req("met.praesentation", 3),
        req("fue.changeManagement", 2),
        req("met.selbstorganisation", 3),
        req("dig.aiRisiken", 2, "regulatorik"),
    ],
    operations: [
        req("reg.gwg", 2, "regulatorik"),
        req("reg.vsb", 2, "regulatorik"),
        req("reg.datenschutz", 2, "regulatorik"),
        req("reg.infosec", 2, "regulatorik"),
        req("fach.zahlungsverkehr", 3),
        req("fach.produktkenntnis", 2),
        req("met.selbstorganisation", 2),
        req("dig.aiRisiken", 2, "regulatorik"),
    ],
    "hr-specialist": [
        req("reg.datenschutz", 3, "regulatorik"),
        req("reg.infosec", 2, "regulatorik"),
        req("fue.arbeitsrecht", 3),
        req("fue.rekrutierung", 3),
        req("fue.absenzen", 2),
        req("met.schreiben", 3),
        req("dig.aiRisiken", 2, "regulatorik"),
    ],
    teamleiter: [
        req("reg.datenschutz", 2, "regulatorik"),
        req("reg.infosec", 2, "regulatorik"),
        req("fue.mag", 3),
        req("fue.feedback", 3),
        req("fue.delegation", 3),
        req("fue.konflikt", 2),
        req("fue.arbeitsrecht", 2),
        req("fue.teamentwicklung", 2),
        req("dig.aiRisiken", 2, "regulatorik"),
    ],
    lernender: [
        req("reg.datenschutz", 1, "regulatorik"),
        req("reg.infosec", 2, "regulatorik"),
        req("reg.bankkundengeheimnis", 2),
        req("fach.produktkenntnis", 2),
        req("fach.beratungsprozess", 1),
        req("fach.zahlungsverkehr", 2),
        req("met.selbstorganisation", 2),
        req("dig.aiRisiken", 2, "regulatorik"),
    ],
}

/**
 * Career aspirations — the "Karriereziel" lever. Requirements listed here are
 * additive on top of the current role and carry origin `ziel`, which is what
 * lets the composer drop them first when the budget is tight (a development
 * wish yields to a regulatory duty, never the other way round).
 */
export interface Aspiration {
    id: string
    label: string
}

export const ASPIRATIONS: Aspiration[] = [
    { id: "none", label: "Kein Ziel angegeben" },
    { id: "teamleitung", label: "Teamleitung anstreben" },
    { id: "finanzplanung", label: "Richtung Finanzplanung" },
    { id: "kreditspezialist", label: "Kreditspezialist:in werden" },
    { id: "projektleitung", label: "Richtung Projektleitung" },
    { id: "anlagespezialist", label: "Anlagespezialist:in werden" },
    { id: "nachhaltigkeit", label: "Richtung Nachhaltigkeit und ESG" },
    { id: "compliance", label: "Richtung Compliance" },
    { id: "datenanalyse", label: "Richtung Daten und Analyse" },
]

export const ASPIRATION_REQUIREMENTS: Record<string, SkillRequirement[]> = {
    none: [],
    teamleitung: [
        req("fue.feedback", 3, "ziel"),
        req("fue.mag", 2, "ziel"),
        req("fue.delegation", 2, "ziel"),
        req("fue.konflikt", 2, "ziel"),
        req("fue.rekrutierung", 2, "ziel"),
    ],
    finanzplanung: [
        req("fach.finanzplanung", 3, "ziel"),
        req("fach.vorsorge", 3, "ziel"),
        req("fach.steuern", 2, "ziel"),
    ],
    kreditspezialist: [
        req("fach.bilanzanalyse", 3, "ziel"),
        req("fach.kreditrisiko", 3, "ziel"),
        req("fach.firmenkunden", 2, "ziel"),
    ],
    projektleitung: [
        req("met.projektmanagement", 3, "ziel"),
        req("met.agile", 2, "ziel"),
        req("met.praesentation", 3, "ziel"),
    ],
    anlagespezialist: [
        req("fach.anlageberatung", 3, "ziel"),
        req("fach.portfolio", 3, "ziel"),
        req("fach.wertschriften", 3, "ziel"),
    ],
    nachhaltigkeit: [
        req("fach.esg", 3, "ziel"),
        req("fach.anlageberatung", 2, "ziel"),
    ],
    compliance: [
        req("reg.gwg", 3, "ziel"),
        req("reg.kyc", 3, "ziel"),
        req("reg.marktverhalten", 2, "ziel"),
    ],
    datenanalyse: [
        req("dig.datenanalyse", 3, "ziel"),
        req("dig.excel", 3, "ziel"),
        req("met.prozessmanagement", 2, "ziel"),
    ],
}
